import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Typography, styled, InputAdornment } from '@mui/material';
import Grid from '../components/Grid';
import StatusBadge from '../components/StatusBadge';
import Checkbox from '../components/Checkbox';
import InputField from '../components/InputField';
import {
  FilterArrow,
  FilterButton,
  FilterButtonCaption,
  FilterButtonLabel,
  FilterMenu,
  FilterMenuItem,
  FilterToolbar,
  GridHeader,
  SearchInputWrapper,
  SortMenuItem,
  SortMenuSection,
  SortMenuSectionHeader,
  ThemedSearchIcon,
} from '../components/FilterToolbarStyles';
import { formatDate } from '../helpers/date';
import { useUsers } from '../hooks/user';
import api from '../services/api';
import { PLATFORM_ICONS, SERVICE_OPTIONS, SERVICE_STATUS_VARIANTS } from '../constants/service';
import {
  GiveBonusDialog,
  RemoveAccountConfirmationDialog,
  DriversFooterRow,
  DriversActionsCell,
  DriversCheckboxCell,
  DriversCheckboxHeader,
} from '../components/drivers';
import AddBalanceDialog from '../components/AddBalanceDialog';
import { getUserStatusLabel, normalizeUserStatus } from '../helpers/status';
import { USER_STATUS_OPTIONS } from '../constants/user';
import { THEME_MODES } from '../theme';
import { pathDriver } from '../constants/routes';

const SORT_OPTIONS = {
  alphabetical: {
    label: 'Trier par ordre alphabétique',
    options: [
      { value: 'a-z', label: 'A à Z' },
      { value: 'z-a', label: 'Z à A' },
    ],
  },
  'date-joined': {
    label: "Trier par date d'inscription",
    options: [
      { value: 'newest', label: 'Récent → Ancien' },
      { value: 'oldest', label: 'Ancien → Récent' },
    ],
  },
  points: {
    label: 'Trier par points',
    options: [
      { value: 'most', label: 'Du plus au moins' },
      { value: 'least', label: 'Du moins au plus' },
    ],
  },
};

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  margin: '0px 32px 32px 0px',
  overflow: 'hidden',
  borderRadius: 28,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
}));

const getRowId = (params) => params.data?.id;
const isFullWidthRow = ({ rowNode }) => rowNode?.data?.id === 'footer';

const DriversScreen = () => {
  const navigate = useNavigate();
  const { data, loading, refetch } = useUsers();
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterOpen = Boolean(filterAnchorEl);
  const [statusFilterAnchorEl, setStatusFilterAnchorEl] = useState(null);
  const statusFilterOpen = Boolean(statusFilterAnchorEl);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const sortOpen = Boolean(sortAnchorEl);
  const [sortConfig, setSortConfig] = useState({ category: 'date-joined', value: 'newest' });
  const [giveBonusDialogOpen, setGiveBonusDialogOpen] = useState(false);
  const [addBalanceDialogOpen, setAddBalanceDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const services = selectedServices;
    const statuses = selectedStatuses;

    return data.filter((user) => {
      const matchesSearch =
        !query ||
        (user.name || '').toLowerCase().includes(query) ||
        (user.email || '').toLowerCase().includes(query);

      const userPlatforms = new Set(
        (user.services || [])
          .filter((s) => s.status === SERVICE_STATUS_VARIANTS.verified)
          .map((s) => s?.platform)
      );

      const matchesServices = !services.size || [...services].some((p) => userPlatforms.has(p));
      const matchesStatus = !statuses.size || statuses.has(normalizeUserStatus(user));

      return matchesSearch && matchesServices && matchesStatus;
    });
  }, [data, searchQuery, selectedServices, selectedStatuses]);

  const sortedData = useMemo(() => {
    const { category, value } = sortConfig;
    const arr = [...filteredData];

    if (category === 'alphabetical') {
      arr.sort((a, b) => {
        const nameA = (a.name || '').trim().toLowerCase();
        const nameB = (b.name || '').trim().toLowerCase();
        const cmp = nameA.localeCompare(nameB);
        return value === 'z-a' ? -cmp : cmp;
      });
    } else if (category === 'date-joined') {
      arr.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return value === 'oldest' ? dateA - dateB : dateB - dateA;
      });
    } else if (category === 'points') {
      arr.sort((a, b) => {
        const rankA = Number(a.rank) || 0;
        const rankB = Number(b.rank) || 0;
        return value === 'least' ? rankA - rankB : rankB - rankA;
      });
    }
    return arr;
  }, [filteredData, sortConfig]);

  const rows = useMemo(() => {
    return sortedData.map((user) => ({
      ...user,
      isChecked: checkedIds.has(user.id),
    }));
  }, [sortedData, checkedIds]);

  const handleToggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setCheckedIds((prev) => (prev.size ? new Set() : new Set(rows.map((r) => r.id))));
  }, [rows]);

  const handleFilterOpen = (e) => setFilterAnchorEl(e.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);
  const handleStatusFilterOpen = (e) => setStatusFilterAnchorEl(e.currentTarget);
  const handleStatusFilterClose = () => setStatusFilterAnchorEl(null);
  const handleSortOpen = (e) => setSortAnchorEl(e.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortSelect = useCallback((category, value) => {
    setSortConfig({ category, value });
    setSortAnchorEl(null);
  }, []);

  const handleServiceToggle = useCallback((key) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleStatusToggle = useCallback((status) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  }, []);

  const handleOpenGiveBonus = useCallback(() => {
    setGiveBonusDialogOpen(true);
  }, []);

  const handleOpenAddBalance = useCallback(() => {
    setAddBalanceDialogOpen(true);
  }, []);

  const handleOpenRemove = useCallback((ids) => {
    if (!ids?.length) return;
    setRemoveDialogOpen(true);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    const userIds = selectedRow ? [selectedRow.id] : Array.from(checkedIds);
    if (!userIds.length) return;
    const response = await api.post('/users/remove', { userIds });
    if (!response?.ok) return;

    refetch();
    setCheckedIds(new Set());
    setRemoveDialogOpen(false);
    setSelectedRow(null);
  }, [checkedIds, refetch, selectedRow]);

  const handleSelectRow = useCallback((row) => {
    setSelectedRow(row);
  }, []);

  const handleGiveBonusSubmit = useCallback(
    async (amount) => {
      const userIds = selectedRow ? [selectedRow.id] : Array.from(checkedIds);
      if (!userIds.length) return;

      const response = await api.post('/users/income', { amount, userIds });
      if (!response?.ok) return;

      refetch();
      setGiveBonusDialogOpen(false);
      setCheckedIds(new Set());
      setSelectedRow(null);
    },
    [selectedRow, checkedIds, refetch]
  );

  const handleAddBalanceSubmit = useCallback(
    async ({ amount, platform }) => {
      const userId = selectedRow?.id;

      if (!userId) return;

      const response = await api.post('/users/income', { amount, platform, userIds: [userId] });
      if (!response?.ok) return;

      refetch();
      setAddBalanceDialogOpen(false);
      setSelectedRow(null);
    },
    [refetch, selectedRow?.id]
  );

  const filterLabel =
    selectedServices.size === 0
      ? 'Aucun'
      : selectedServices.size === SERVICE_OPTIONS.length
        ? 'Tous'
        : `${selectedServices.size} sélectionné(s)`;

  const statusFilterLabel =
    selectedStatuses.size === 0
      ? 'Aucun'
      : selectedStatuses.size === USER_STATUS_OPTIONS.length
        ? 'Tous'
        : `${selectedStatuses.size} sélectionné(s)`;

  const fullWidthCellRenderer = useCallback(
    (params) => (
      <DriversFooterRow
        data={params.data}
        onGiveBonus={handleOpenGiveBonus}
        onRemove={() => handleOpenRemove(Array.from(checkedIds))}
      />
    ),
    [handleOpenGiveBonus, handleOpenRemove, checkedIds]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerComponent: DriversCheckboxHeader,
        cellRenderer: DriversCheckboxCell,
        width: 63,
        maxWidth: 63,
        minWidth: 63,
        pinned: 'left',
      },
      {
        field: 'name',
        headerName: 'Nom',
        flex: 1,
        minWidth: 232,
        headerAlign: 'left',
        cellClass: 'ag-cell-left',
        cellRenderer: ({ data }) => (
          <Box
            onClick={() => navigate(pathDriver(data.id))}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 },
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{data.name}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
              {data.email}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'date',
        headerName: "Date d'inscription",
        initialWidth: 217,
        valueFormatter: ({ data }) => formatDate(data.createdAt),
      },
      {
        field: 'status',
        headerName: 'Statut',
        cellRenderer: ({ data }) => {
          const status = normalizeUserStatus(data);
          return <StatusBadge variant={status}>{getUserStatusLabel(status)}</StatusBadge>;
        },
      },
      {
        field: 'services',
        headerName: 'Applis',
        cellRenderer: ({ data }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {data.services
              ?.filter((s) => s.status === SERVICE_STATUS_VARIANTS.verified)
              .map((service) => {
                const PlatformIcon = PLATFORM_ICONS[service.platform];
                if (!PlatformIcon) return null;
                return <Box key={service.platform} component={PlatformIcon} />;
              })}
          </Box>
        ),
      },
      {
        field: 'rank',
        headerName: 'Points',
        cellRenderer: ({ data }) => (
          <StatusBadge sx={{ borderRadius: '4px' }}>
            <Typography sx={{ fontSize: 16, fontWeight: 900 }}>{String(data.rank)}</Typography>
          </StatusBadge>
        ),
      },
      {
        field: 'revenue',
        headerName: 'Revenus (€)',
        valueFormatter: ({ data }) => data.revenue || '',
        headerAlign: 'right',
        cellClass: 'ag-cell-right',
      },
      {
        field: 'contributionPercentage',
        headerName: 'Contribution (%)',
        initialWidth: 170,
        valueFormatter: ({ data }) => `${data.contributionPercentage ?? 15}`,
        headerAlign: 'right',
        cellClass: 'ag-cell-right',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        cellClass: 'actions-cell ag-cell-right',
        headerAlign: 'right',
        cellRenderer: DriversActionsCell,
        cellRendererParams: {
          onGiveBonus: handleOpenGiveBonus,
          onAddBalance: handleOpenAddBalance,
          onSelectRow: handleSelectRow,
          onRemove: (row) => handleOpenRemove([row.id]),
        },
      },
    ],
    [handleOpenGiveBonus, handleOpenAddBalance, handleSelectRow, navigate, handleOpenRemove]
  );

  const isAllChecked = checkedIds.size > 0 && rows.length === checkedIds.size;

  const defaultColDef = useMemo(
    () => ({
      initialWidth: 170,
      cellRendererParams: {
        onToggle: handleToggle,
      },
      headerComponentParams: {
        isAllChecked,
        onToggleAll: handleToggleAll,
      },
    }),
    [handleToggle, isAllChecked, handleToggleAll]
  );

  const pinnedBottomRowData = useMemo(
    () => (checkedIds.size ? [{ id: 'footer', size: checkedIds.size }] : undefined),
    [checkedIds.size]
  );

  return (
    <Body>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <GridHeader>
          <Typography sx={{ fontSize: 22, fontWeight: 500, letterSpacing: 0.06 }}>
            Chauffeurs
          </Typography>
        </GridHeader>

        <FilterToolbar>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <SearchInputWrapper>
              <InputField
                fullWidth
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  shrink: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      <ThemedSearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                }}
              />
            </SearchInputWrapper>

            <FilterButton
              $open={statusFilterOpen}
              onClick={handleStatusFilterOpen}
              aria-haspopup="true"
              aria-expanded={statusFilterOpen ? 'true' : undefined}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
              >
                <FilterButtonCaption>Statut</FilterButtonCaption>
                <FilterButtonLabel>{statusFilterLabel}</FilterButtonLabel>
              </Box>
              <FilterArrow open={statusFilterOpen} />
            </FilterButton>

            <FilterMenu
              anchorEl={statusFilterAnchorEl}
              open={statusFilterOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                  handleStatusFilterClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {USER_STATUS_OPTIONS.map((status) => (
                <FilterMenuItem
                  key={status}
                  onClick={() => handleStatusToggle(status)}
                  disableRipple
                  sx={{ justifyContent: 'flex-start' }}
                >
                  <Checkbox checked={selectedStatuses.has(status)} />
                  <StatusBadge variant={status}>{getUserStatusLabel(status)}</StatusBadge>
                </FilterMenuItem>
              ))}
            </FilterMenu>

            <FilterButton
              $open={filterOpen}
              onClick={handleFilterOpen}
              aria-haspopup="true"
              aria-expanded={filterOpen ? 'true' : undefined}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
              >
                <FilterButtonCaption>Applis</FilterButtonCaption>
                <FilterButtonLabel>{filterLabel}</FilterButtonLabel>
              </Box>
              <FilterArrow open={filterOpen} />
            </FilterButton>

            <FilterMenu
              anchorEl={filterAnchorEl}
              open={filterOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleFilterClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {SERVICE_OPTIONS.map(({ key, label, icon: Icon }) => (
                <FilterMenuItem key={key} onClick={() => handleServiceToggle(key)} disableRipple>
                  <Checkbox checked={selectedServices.has(key)} />
                  <Typography sx={{ fontSize: 16, fontWeight: 500, flex: 1 }}>{label}</Typography>
                  {Icon && <Box component={Icon} sx={{ width: 32, height: 32, flexShrink: 0 }} />}
                </FilterMenuItem>
              ))}
            </FilterMenu>
          </Box>

          <FilterButton
            $open={sortOpen}
            onClick={handleSortOpen}
            aria-haspopup="true"
            aria-expanded={sortOpen ? 'true' : undefined}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
            >
              <FilterButtonCaption>{SORT_OPTIONS[sortConfig.category].label}</FilterButtonCaption>
              <FilterButtonLabel>
                {SORT_OPTIONS[sortConfig.category].options.find((o) => o.value === sortConfig.value)
                  ?.label ?? 'Récent → Ancien'}
              </FilterButtonLabel>
            </Box>
            <FilterArrow open={sortOpen} />
          </FilterButton>

          <FilterMenu
            anchorEl={sortAnchorEl}
            open={sortOpen}
            onClose={(_, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleSortClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {Object.entries(SORT_OPTIONS).map(([category, { options }]) => (
              <SortMenuSection key={category}>
                <SortMenuSectionHeader>
                  {category === 'alphabetical'
                    ? 'ALPHABÉTIQUE'
                    : category === 'date-joined'
                      ? "DATE D'INSCRIPTION"
                      : 'POINTS'}
                </SortMenuSectionHeader>
                {options.map(({ value, label }) => (
                  <SortMenuItem
                    key={value}
                    $selected={sortConfig.category === category && sortConfig.value === value}
                    onClick={() => handleSortSelect(category, value)}
                    disableRipple
                  >
                    {label}
                  </SortMenuItem>
                ))}
              </SortMenuSection>
            ))}
          </FilterMenu>
        </FilterToolbar>

        <Grid
          loading={loading}
          animateRows
          suppressScrollOnNewData
          getRowId={getRowId}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          fullWidthCellRenderer={fullWidthCellRenderer}
          pinnedBottomRowData={pinnedBottomRowData}
          isFullWidthRow={isFullWidthRow}
          sx={{
            '& .ag-row:hover .ag-cell.actions-cell .action-icons': {
              opacity: 1,
            },
          }}
        />
        <GiveBonusDialog
          open={giveBonusDialogOpen}
          onClose={() => {
            setGiveBonusDialogOpen(false);
            setSelectedRow(null);
          }}
          onSubmit={handleGiveBonusSubmit}
        />
        <AddBalanceDialog
          open={addBalanceDialogOpen}
          onClose={() => {
            setAddBalanceDialogOpen(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddBalanceSubmit}
        />
        <RemoveAccountConfirmationDialog
          open={removeDialogOpen}
          onClose={() => {
            setRemoveDialogOpen(false);
            setSelectedRow(null);
          }}
          onConfirm={handleRemoveConfirm}
        />
      </Box>
    </Body>
  );
};

export default DriversScreen;
